import React, { useCallback, useState } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "react-hot-toast";
import { BiLoaderCircle } from "react-icons/bi";
import { FiUploadCloud } from "react-icons/fi";

const Uploader = ({ setImage, image }) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  // upload file
  const onDrop = useCallback(
    async (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please upload an image file (jpeg, png)");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }

      // Create preview URL
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);

      // Pass file to parent component
      if (setImage) {
        setImage(file);
      }

      toast.success("Image selected successfully!");
    },
    [setImage]
  );

  // Cleanup preview URL on unmount
  React.useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: false,
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
    },
    onDrop,
  });

  // Use previewUrl if available, otherwise use image prop (for existing images)
  const displayImage = previewUrl || image;

  return (
    <div className='grid w-full grid-cols-12 gap-4 text-center'>
      <div
        className={`col-span-12 cursor-pointer rounded-md border-2 border-dashed px-6 pb-6 pt-5 transition-colors sm:col-span-8 lg:col-span-10 ${
          isDragActive ? "border-subMain bg-subMain/5" : "border-border"
        }`}
        {...getRootProps()}
      >
        <input {...getInputProps()} />
        <span className='mx-auto flex justify-center'>
          <FiUploadCloud
            className={`text-3xl ${isDragActive ? "text-subMain" : "text-subMain"}`}
          />
        </span>
        <p className='mt-2 text-sm'>
          {isDragActive ? "Drop your image here..." : "Drag your image here"}
        </p>
        <em className='text-xs text-gray-400'>
          (Only *.jpeg and *.png images will be accepted)
        </em>
      </div>
      {/* image preview */}
      <div className='col-span-12 sm:col-span-4 lg:col-span-2'>
        {loading ? (
          <div className='flex-colo h-32 w-full rounded-md border-2 border-dashed border-border bg-dry px-6'>
            <BiLoaderCircle className='mx-auto animate-spin text-3xl text-main' />
            <span className='mt-2 text-sm text-text'>Uploading...</span>
          </div>
        ) : displayImage ? (
          <img
            src={displayImage}
            alt='preview'
            className='h-32 w-full rounded border border-border object-cover'
          />
        ) : (
          <div className='flex-colo h-32 w-full rounded-md border-2 border-dashed border-border bg-dry'>
            <span className='text-xs text-gray-400'>No image</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default Uploader;
